import { skipToken } from '@reduxjs/toolkit/query/react';
import { FormSection } from '@strata/react-form-section';
import { IntakeCaseTimeline, ReferralTimeline } from '@strata/react-timeline';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';

import { LiveReferralDetails } from 'components/referrals/LiveReferralDetails';
import { MemberDetails } from 'components/referrals/MemberDetails';
import { PharmacyDetails } from 'components/referrals/PharmacyDetails';
import { PrescriberDetails } from 'components/referrals/PrescriberDetails';
import { ReferralDetails } from 'components/referrals/ReferralDetails';
import {
    buildIntakeCaseMilestoneReferralFieldsFromTreatment,
    useIntakeCaseMilestones,
} from 'services/intakeCaseHelpers';
import { type BackToDestination } from 'services/navigationHelpers';
import { useEnrichedReferralMilestones } from 'services/referralMilestoneHelpers';
import { useAppSelector } from 'store';
import { useGetPrescriberDetailsQuery, useGetSupplierDetailsQuery } from 'store/api/referenceApi';
import { type TreatmentDetails } from 'store/api/treatmentApi';
import { useGetReferralDetailsQuery } from 'store/api/treatmentApi';

interface ReferralDetailsOverviewProps {
    referralId: string;
    showMemberDetails?: boolean;
    currentLocation: BackToDestination;
    treatmentDetailsForIntakeMilestones?: TreatmentDetails;
}

export const ReferralDetailsOverview = ({
    referralId,
    showMemberDetails = false,
    currentLocation,
    treatmentDetailsForIntakeMilestones = undefined,
}: ReferralDetailsOverviewProps) => {
    const { payerId } = useAppSelector((state) => state.auth);
    const { showIntakeTimeline: intakeTimelineEnabledFlag } = useFlags<{
        showIntakeTimeline: boolean;
    }>();

    const { data: referralDetails } = useGetReferralDetailsQuery({ payerId: payerId!, referralId });

    const intakeMilestoneReferralFields = useMemo(
        () =>
            buildIntakeCaseMilestoneReferralFieldsFromTreatment(
                treatmentDetailsForIntakeMilestones,
                referralId,
            ),
        [treatmentDetailsForIntakeMilestones, referralId],
    );
    const { data: supplierDetails, isFetching: isSupplierDetailsLoading } =
        useGetSupplierDetailsQuery(
            referralDetails?.supplierNpi ? { npi: referralDetails.supplierNpi } : skipToken,
        );
    const { data: prescriberDetails, isFetching: isPrescriberDetailsLoading } =
        useGetPrescriberDetailsQuery(
            referralDetails?.prescriberNpi ? { npi: referralDetails?.prescriberNpi } : skipToken,
        );

    const { enrichedMilestones, referralMilestonesFetching } =
        useEnrichedReferralMilestones(referralId);

    const { milestones, milestonesFetching, intakeCasesVisible } = useIntakeCaseMilestones({
        referralFields: intakeMilestoneReferralFields ?? referralDetails,
        showIntakeTimeline: intakeTimelineEnabledFlag,
    });
    const shouldRenderIntakeTimeline =
        intakeTimelineEnabledFlag && intakeCasesVisible && milestones.length > 0;

    return (
        <div className="sds_referral-details-overview">
            <div className="sds_referral-details-overview__container" style={{ flex: '2 0' }}>
                {showMemberDetails && <MemberDetails referralDetails={referralDetails} />}
                <LiveReferralDetails referralDetails={referralDetails} />
                <ReferralDetails
                    referralDetails={referralDetails}
                    currentLocation={currentLocation}
                />
                <div className="sds_details-page-overview__container--row">
                    <PrescriberDetails
                        prescriberDetails={prescriberDetails}
                        isLoading={isPrescriberDetailsLoading}
                    />
                    <PharmacyDetails
                        supplierDetails={supplierDetails}
                        isLoading={isSupplierDetailsLoading}
                        referralStatus={referralDetails?.referralStatus}
                    />
                </div>
            </div>
            <div className="sds_referral-details-overview__container" style={{ flex: '1 0' }}>
                {shouldRenderIntakeTimeline && (
                    <FormSection
                        sectionTitle="Prior Auth Request Timeline"
                        isLoading={milestonesFetching}
                        content={<IntakeCaseTimeline milestones={milestones} />}
                    />
                )}
                <FormSection
                    sectionTitle="Referral Timeline"
                    isLoading={referralMilestonesFetching}
                    content={<ReferralTimeline referralMilestones={enrichedMilestones} />}
                />
            </div>
        </div>
    );
};
